import {
  journeyAnalytics,
  type StartedEvent,
} from "@src/features/analytics/journeyAnalytics"
import {
  type ClientOrderId,
  createClientOrderId,
} from "@src/features/trade/hyperliquid/clientOrderId"
import {
  getHLBuilderAddress,
  HL_BUILDER_FEE,
  HL_MIN_ORDER_VALUE_USD,
} from "@src/features/trade/hyperliquid/constants/config"
import {
  getDexFromCoin,
  isBuilderDexOrderAssetId,
} from "@src/features/trade/hyperliquid/marketIdentity"
import {
  type EntryOrder,
  getEntryOrderPrice,
} from "@src/features/trade/hyperliquid/orderIntent"
import { buildOpenOrderSizePlan } from "@src/features/trade/hyperliquid/orderSizing"
import {
  addTPSLOrders,
  approveHLBuilderFee,
  cancelOrders,
  closePosition,
  type EntryOrderResult,
  modifyTPSLOrders,
  needsBuilderApproval,
  placeEntryOrder,
  setBuilderDexAbstraction,
  updateLeverage,
} from "@src/features/trade/hyperliquid/services/hyperliquidExchangeService"
import {
  getAccountState,
  getMaxBuilderFee,
  getOpenOrders,
  getUserAbstraction,
  supportsBuilderDexCollateral,
} from "@src/features/trade/hyperliquid/services/hyperliquidService"
import type { HLOpenOrder, Result } from "@src/features/trade/hyperliquid/types"
import type { TradingSigner } from "@src/features/tradingSigner"
import { assign, emit, enqueueActions, fromPromise, setup } from "xstate"

const DEX_ABSTRACTION_CHECK_ATTEMPTS = 3
const DEX_ABSTRACTION_UNKNOWN_ERROR =
  "Could not verify builder-dex collateral mode. Please retry."

type OpenTradeActionInput = {
  tradingSigner: TradingSigner
  coin?: string
  orderAssetId: number
  szDecimals: number
  currentLeverage: number | null
  currentLeverageMode: "cross" | "isolated" | null
  selectedLeverage: number
  margin: number
  entryOrder: EntryOrder
  isLong: boolean
  takeProfitPrice?: number
  stopLossPrice?: number
  cloid?: ClientOrderId
  takeProfitCloid?: ClientOrderId
  stopLossCloid?: ClientOrderId
  analytics?: PerpsOrderJourney
}

type SaveAutoCloseActionInput = {
  tradingSigner: TradingSigner
  coin?: string
  orderAssetId: number
  szDecimals: number
  isLong: boolean
  size: string
  currentTpOrder?: HLOpenOrder
  currentSlOrder?: HLOpenOrder
  desiredTakeProfitPrice?: number
  desiredStopLossPrice?: number
}

type ClosePositionActionInput = {
  tradingSigner: TradingSigner
  coin?: string
  orderAssetId: number
  szDecimals: number
  isLong: boolean
  size: string
  currentPrice: number
  cloid?: ClientOrderId
  analytics?: PerpsCloseJourney
}

type PerpsOrderJourney = Extract<
  StartedEvent,
  { journey: { flow: "perps_order" } }
>
type PerpsCloseJourney = Extract<
  StartedEvent,
  { journey: { flow: "perps_position_close" } }
>

type OpenActionPlan =
  | {
      ok: true
      requestedSize: string
      needsLeverageUpdate: boolean
      hasAutoClose: boolean
    }
  | {
      ok: false
      error: string
    }

type AutoCloseActionPlan = {
  noOp: boolean
  cancels: Array<{ orderId: number }>
  modifies: Array<{ orderId: number; leg: "tp" | "sl"; price: number }>
  additions: Array<{ leg: "tp" | "sl"; price: number; cloid: ClientOrderId }>
}

type AutoCloseReconcileInput = {
  kind: "add"
  action: SaveAutoCloseActionInput
  plan: AutoCloseActionPlan
}

type AutoCloseReconcileResult = {
  updated: boolean
  updatedLegs: Array<"tp" | "sl">
}

type DexAbstractionCheckResult =
  | { status: "ready" }
  | { status: "needsSetup" }
  | { status: "unknown"; error: string }

type PendingRetryTarget =
  | "opening"
  | "addingAutoClose"
  | "closingPosition"
  | "evaluateOpenPlan"
  | null

type PositionScreenContext = {
  tx: {
    kind:
      | "idle"
      | "settingDexAbstraction"
      | "settingLeverage"
      | "opening"
      | "updatingAutoClose"
      | "closingPosition"
      | "approvingBuilder"
    error: string | null
    partialSuccess: string | null
    failedKind:
      | "settingDexAbstraction"
      | "settingLeverage"
      | "opening"
      | "updatingAutoClose"
      | "closingPosition"
      | "approvingBuilder"
      | null
    needsLeverage: boolean
    pendingRetry: PendingRetryTarget
    builderApprovalAttempted: boolean
  }
  openAction: OpenTradeActionInput | null
  openPlan: OpenActionPlan | null
  autoCloseAction: SaveAutoCloseActionInput | null
  autoClosePlan: AutoCloseActionPlan | null
  closeAction: ClosePositionActionInput | null
}

type PositionScreenEvent =
  | { type: "SUBMIT_OPEN"; input: OpenTradeActionInput }
  | { type: "SAVE_AUTO_CLOSE"; input: SaveAutoCloseActionInput }
  | { type: "SUBMIT_CLOSE"; input: ClosePositionActionInput }
  | { type: "DISMISS_TX_RESULT" }

type PositionScreenEmittedEvent =
  | { type: "POSITION_OPENED" }
  | { type: "POSITION_OPEN_ORDER_PLACED" }
  | { type: "POSITION_OPEN_PARTIAL" }
  | { type: "AUTO_CLOSE_UPDATED"; updatedLegs: Array<"tp" | "sl"> }
  | { type: "POSITION_CLOSED" }

export const positionScreenMachine = setup({
  types: {
    context: {} as PositionScreenContext,
    events: {} as PositionScreenEvent,
    emitted: {} as PositionScreenEmittedEvent,
  },
  actors: {
    updateLeverageActor: fromPromise(
      async ({
        input,
      }: {
        input: Pick<
          OpenTradeActionInput,
          "tradingSigner" | "orderAssetId" | "selectedLeverage"
        >
      }) =>
        updateLeverage(input.tradingSigner, {
          orderAssetId: input.orderAssetId,
          leverage: input.selectedLeverage,
        })
    ),
    entryOrderActor: fromPromise(
      async ({
        input,
      }: {
        input: Pick<
          OpenTradeActionInput,
          | "tradingSigner"
          | "orderAssetId"
          | "szDecimals"
          | "isLong"
          | "entryOrder"
          | "cloid"
          | "takeProfitPrice"
          | "stopLossPrice"
          | "takeProfitCloid"
          | "stopLossCloid"
        > & { requestedSize: string }
      }) =>
        placeEntryOrder(input.tradingSigner, {
          orderAssetId: input.orderAssetId,
          szDecimals: input.szDecimals,
          isLong: input.isLong,
          size: input.requestedSize,
          entryOrder: input.entryOrder,
          cloid: input.cloid,
          takeProfitPrice: input.takeProfitPrice,
          stopLossPrice: input.stopLossPrice,
          takeProfitCloid: input.takeProfitCloid,
          stopLossCloid: input.stopLossCloid,
        })
    ),
    cancelOrdersActor: fromPromise(
      async ({
        input,
      }: {
        input: Pick<
          SaveAutoCloseActionInput,
          "tradingSigner" | "orderAssetId"
        > & {
          cancels: Array<{ orderId: number }>
        }
      }) =>
        cancelOrders(input.tradingSigner, {
          cancels: input.cancels.map((cancel) => ({
            orderAssetId: input.orderAssetId,
            orderId: cancel.orderId,
          })),
        })
    ),
    modifyTPSLActor: fromPromise(
      async ({
        input,
      }: {
        input: SaveAutoCloseActionInput & {
          modifies: AutoCloseActionPlan["modifies"]
        }
      }) =>
        modifyTPSLOrders(input.tradingSigner, {
          modifies: input.modifies.map((modify) => ({
            orderId: modify.orderId,
            orderAssetId: input.orderAssetId,
            szDecimals: input.szDecimals,
            isLong: input.isLong,
            size: input.size,
            leg: modify.leg,
            price: modify.price,
          })),
        })
    ),
    addTPSLActor: fromPromise(
      async ({
        input,
      }: {
        input: SaveAutoCloseActionInput & {
          additions: AutoCloseActionPlan["additions"]
        }
      }) =>
        addTPSLOrders(input.tradingSigner, {
          additions: input.additions.map((addition) => ({
            orderAssetId: input.orderAssetId,
            szDecimals: input.szDecimals,
            isLong: input.isLong,
            size: input.size,
            leg: addition.leg,
            price: addition.price,
            cloid: addition.cloid,
          })),
        })
    ),
    closePositionActor: fromPromise(
      async ({ input }: { input: ClosePositionActionInput }) =>
        closePosition(input.tradingSigner, {
          orderAssetId: input.orderAssetId,
          szDecimals: input.szDecimals,
          isLong: input.isLong,
          size: input.size,
          currentPrice: input.currentPrice,
          cloid: input.cloid,
        })
    ),
    reconcileCloseActor: fromPromise(
      async ({ input }: { input: ClosePositionActionInput }) =>
        reconcileCloseSubmission(input)
    ),
    reconcileAutoCloseActor: fromPromise(
      async ({
        input,
      }: {
        input: AutoCloseReconcileInput
      }): Promise<AutoCloseReconcileResult> =>
        reconcileAutoCloseSubmission(input)
    ),
    approveBuilderFeeActor: fromPromise(
      async ({ input }: { input: { tradingSigner: TradingSigner } }) =>
        approveHLBuilderFee(input.tradingSigner)
    ),
    checkBuilderApprovalActor: fromPromise(
      async ({
        input,
      }: {
        input: { user: `0x${string}` | null }
      }): Promise<{ approved: boolean }> => {
        if (!input.user) return { approved: true }
        const builder = getHLBuilderAddress()
        if (!builder) {
          throw new Error(
            "Hyperliquid builder address is not configured (NEXT_PUBLIC_HL_BUILDER_ADDRESS)"
          )
        }
        const result = await getMaxBuilderFee(input.user, builder)
        // Treat lookup errors as "approved" — the order's lazy retry will
        // recover if HL actually rejects.
        if (!result.ok) return { approved: true }
        return { approved: result.data >= HL_BUILDER_FEE }
      }
    ),
    checkDexAbstractionActor: fromPromise(
      async ({
        input,
      }: {
        input: { user: `0x${string}` }
      }): Promise<DexAbstractionCheckResult> => {
        let lastError: string | null = null
        for (
          let attempt = 0;
          attempt < DEX_ABSTRACTION_CHECK_ATTEMPTS;
          attempt++
        ) {
          const result = await getUserAbstraction(input.user)
          if (result.ok) {
            return supportsBuilderDexCollateral(result.data)
              ? { status: "ready" }
              : { status: "needsSetup" }
          }
          lastError = result.error
        }
        return {
          status: "unknown",
          error: lastError ?? DEX_ABSTRACTION_UNKNOWN_ERROR,
        }
      }
    ),
    setDexAbstractionActor: fromPromise(
      async ({ input }: { input: { tradingSigner: TradingSigner } }) =>
        setBuilderDexAbstraction(input.tradingSigner)
    ),
  },
  actions: {
    setIdleKind: assign({
      tx: ({ context }) => setTxKind(context, "idle"),
    }),
    clearTxResultAction: assign({
      tx: ({ context }) => clearTxResult(context),
    }),
    setSettingDexAbstractionKind: assign({
      tx: ({ context }) => setTxKind(context, "settingDexAbstraction"),
    }),
    setSettingLeverageKind: assign({
      tx: ({ context }) => setTxKind(context, "settingLeverage"),
    }),
    setOpeningKind: assign({
      tx: ({ context }) => setTxKind(context, "opening"),
    }),
    setUpdatingAutoCloseKind: assign({
      tx: ({ context }) => setTxKind(context, "updatingAutoClose"),
    }),
    setClosingPositionKind: assign({
      tx: ({ context }) => setTxKind(context, "closingPosition"),
    }),
    setApprovingBuilderKind: assign({
      tx: ({ context }) => ({
        ...setTxKind(context, "approvingBuilder"),
        builderApprovalAttempted: true,
      }),
    }),
    markPendingRetry: assign({
      tx: ({ context }, params: { target: PendingRetryTarget }) => ({
        ...context.tx,
        pendingRetry: params.target,
      }),
    }),
    clearPendingRetry: assign({
      tx: ({ context }) => ({ ...context.tx, pendingRetry: null }),
    }),
    storeOpenAction: assign({
      openAction: ({ event }) =>
        event.type === "SUBMIT_OPEN"
          ? {
              ...event.input,
              cloid: event.input.cloid ?? createClientOrderId(),
              takeProfitCloid:
                event.input.takeProfitCloid ??
                ((event.input.takeProfitPrice ?? 0) > 0
                  ? createClientOrderId()
                  : undefined),
              stopLossCloid:
                event.input.stopLossCloid ??
                ((event.input.stopLossPrice ?? 0) > 0
                  ? createClientOrderId()
                  : undefined),
            }
          : null,
      openPlan: () => null,
      tx: ({ context }) => clearTxResult(context),
    }),
    emitOpenStarted: ({ event }) => {
      if (event.type === "SUBMIT_OPEN" && event.input.analytics != null) {
        journeyAnalytics.started(event.input.analytics)
      }
    },
    completeOpenJourney: ({ context, event }) => {
      const action = context.openAction
      if (action?.analytics == null || action.cloid == null) return

      const outcome = getEntryOrderSuccessOutcome(event)
      const hasProtection =
        context.openPlan?.ok === true
          ? context.openPlan.hasAutoClose
          : action.takeProfitPrice != null || action.stopLossPrice != null

      journeyAnalytics.completed({
        ...action.analytics,
        operation: { namespace: "perps_cloid", key: action.cloid },
        result: outcome.kind,
        protection_status: outcome.autoCloseFailed
          ? "failed"
          : hasProtection
            ? "attached"
            : "not_requested",
      })
    },
    buildOpenPlan: assign({
      openPlan: ({ context }) =>
        context.openAction == null ? null : buildOpenPlan(context.openAction),
    }),
    storeAutoCloseAction: assign({
      autoCloseAction: ({ event }) =>
        event.type === "SAVE_AUTO_CLOSE" ? event.input : null,
      autoClosePlan: () => null,
      tx: ({ context }) => clearTxResult(context),
    }),
    buildAutoClosePlan: assign({
      autoClosePlan: ({ context }) =>
        context.autoCloseAction == null
          ? null
          : buildAutoClosePlan(context.autoCloseAction),
    }),
    storeCloseAction: assign({
      closeAction: ({ event }) =>
        event.type === "SUBMIT_CLOSE"
          ? {
              ...event.input,
              cloid: event.input.cloid ?? createClientOrderId(),
            }
          : null,
      tx: ({ context }) => clearTxResult(context),
    }),
    emitCloseStarted: ({ event }) => {
      if (event.type === "SUBMIT_CLOSE" && event.input.analytics != null) {
        journeyAnalytics.started(event.input.analytics)
      }
    },
    completeCloseJourney: ({ context }) => {
      const action = context.closeAction
      if (action?.analytics == null || action.cloid == null) return

      journeyAnalytics.completed({
        ...action.analytics,
        operation: { namespace: "perps_cloid", key: action.cloid },
        result: "filled",
      })
    },
    assignPlanError: assign({
      tx: ({ context }) =>
        setTxError(
          context,
          context.openPlan && !context.openPlan.ok
            ? context.openPlan.error
            : "Invalid trade plan",
          null
        ),
    }),
    assignFailureError: assign({
      tx: ({ context, event }) =>
        setTxError(
          context,
          getActorResultError(event),
          context.tx.kind === "idle" ? null : context.tx.kind
        ),
    }),
    handleEntryOrderSuccess: enqueueActions(({ enqueue, event }) => {
      const outcome = getEntryOrderSuccessOutcome(event)

      if (outcome.autoCloseFailed) {
        const partialMessage = getEntryAutoClosePartialMessage(event)
        enqueue(
          assign({
            tx: ({ context }) =>
              setTxPartialSuccess(context, partialMessage, "opening"),
          })
        )
      }

      if (outcome.kind === "resting") {
        enqueue(emit(() => ({ type: "POSITION_OPEN_ORDER_PLACED" as const })))
      } else if (!outcome.autoCloseFailed) {
        enqueue(emit(() => ({ type: "POSITION_OPENED" as const })))
      }

      if (outcome.autoCloseFailed) {
        enqueue(emit(() => ({ type: "POSITION_OPEN_PARTIAL" as const })))
      }

      enqueue(
        assign({
          openAction: () => null,
          openPlan: () => null,
        })
      )
    }),
    recordOpenStages: assign({
      tx: ({ context }) => ({
        ...context.tx,
        needsLeverage:
          context.openPlan?.ok === true && context.openPlan.needsLeverageUpdate,
      }),
    }),
    clearOpenWorkflow: assign({
      openAction: () => null,
      openPlan: () => null,
    }),
    clearAutoCloseWorkflow: assign({
      autoCloseAction: () => null,
      autoClosePlan: () => null,
    }),
    clearCloseWorkflow: assign({
      closeAction: () => null,
    }),
    // Invariant: at most one of openAction / autoCloseAction / closeAction
    // is non-null at a time, so a single failure path can safely clear all.
    clearAllWorkflows: assign({
      openAction: () => null,
      openPlan: () => null,
      autoCloseAction: () => null,
      autoClosePlan: () => null,
      closeAction: () => null,
    }),
    clearAllWorkflowsAndPendingRetry: assign({
      openAction: () => null,
      openPlan: () => null,
      autoCloseAction: () => null,
      autoClosePlan: () => null,
      closeAction: () => null,
      tx: ({ context }) => ({ ...context.tx, pendingRetry: null }),
    }),
    assignBuilderApprovalFailure: assign({
      tx: ({ context, event }) => {
        const inner = getActorResultError(event)
        const next = setTxError(
          context,
          inner
            ? `Builder fee approval failed: ${inner}`
            : "Builder fee approval failed",
          "approvingBuilder"
        )
        return next
      },
    }),
    assignDexAbstractionCheckFailure: assign({
      tx: ({ context, event }) => {
        const maybeEvent = event as {
          output?: { status?: string; error?: string }
          error?: unknown
        }
        const inner =
          maybeEvent.output?.status === "unknown"
            ? maybeEvent.output.error
            : getActorResultError(event)
        return setTxError(
          context,
          inner
            ? `${DEX_ABSTRACTION_UNKNOWN_ERROR} ${inner}`
            : DEX_ABSTRACTION_UNKNOWN_ERROR,
          "settingDexAbstraction"
        )
      },
    }),
    emitAutoCloseUpdated: emit(({ context }) => ({
      type: "AUTO_CLOSE_UPDATED" as const,
      updatedLegs: getUpdatedLegs(
        context.autoClosePlan,
        context.autoCloseAction
      ),
    })),
    emitPositionClosed: emit(() => ({ type: "POSITION_CLOSED" as const })),
  },
}).createMachine({
  id: "positionScreen",
  context: {
    tx: {
      kind: "idle",
      error: null,
      partialSuccess: null,
      failedKind: null,
      needsLeverage: false,
      pendingRetry: null,
      builderApprovalAttempted: false,
    },
    openAction: null,
    openPlan: null,
    autoCloseAction: null,
    autoClosePlan: null,
    closeAction: null,
  },
  initial: "idle",
  on: {
    DISMISS_TX_RESULT: {
      actions: "clearTxResultAction",
    },
  },
  states: {
    idle: {
      entry: "setIdleKind",
      on: {
        SUBMIT_OPEN: {
          target: "planningOpen",
          actions: ["storeOpenAction", "emitOpenStarted"],
        },
        SAVE_AUTO_CLOSE: {
          target: "planningAutoClose",
          actions: "storeAutoCloseAction",
        },
        SUBMIT_CLOSE: {
          target: "closingPosition",
          actions: ["storeCloseAction", "emitCloseStarted"],
        },
      },
    },

    planningOpen: {
      entry: ["buildOpenPlan", "recordOpenStages"],
      always: [
        {
          guard: ({ context }) => isOpenPlanInvalid(context.openPlan),
          target: "idle",
          actions: ["assignPlanError", "clearOpenWorkflow"],
        },
        {
          target: "ensuringDexAbstraction",
        },
      ],
    },

    ensuringDexAbstraction: {
      // Only builder dexes (e.g. trade.xyz) need the abstraction check; main HL
      // markets skip straight to builder approval without any extra read.
      always: [
        {
          guard: ({ context }) =>
            !isBuilderDexOrderAssetId(
              assertOpenAction(context.openAction).orderAssetId
            ),
          target: "ensuringBuilderApproval",
        },
      ],
      invoke: {
        src: "checkDexAbstractionActor",
        input: ({ context }) => ({
          user: assertOpenAction(context.openAction).tradingSigner.address,
        }),
        onDone: [
          {
            guard: ({ event }) => event.output.status === "ready",
            target: "ensuringBuilderApproval",
          },
          {
            guard: ({ event }) => event.output.status === "needsSetup",
            target: "settingDexAbstraction",
          },
          {
            target: "idle",
            actions: ["assignDexAbstractionCheckFailure", "clearOpenWorkflow"],
          },
        ],
        onError: {
          target: "idle",
          actions: ["assignDexAbstractionCheckFailure", "clearOpenWorkflow"],
        },
      },
    },

    settingDexAbstraction: {
      entry: "setSettingDexAbstractionKind",
      invoke: {
        src: "setDexAbstractionActor",
        input: ({ context }) => ({
          tradingSigner: assertOpenAction(context.openAction).tradingSigner,
        }),
        onDone: [
          {
            guard: ({ event }) => event.output.ok === false,
            target: "idle",
            actions: ["assignFailureError", "clearOpenWorkflow"],
          },
          {
            target: "ensuringBuilderApproval",
          },
        ],
        onError: {
          target: "idle",
          actions: ["assignFailureError", "clearOpenWorkflow"],
        },
      },
    },

    ensuringBuilderApproval: {
      invoke: {
        src: "checkBuilderApprovalActor",
        input: ({ context }) => {
          const action = assertOpenAction(context.openAction)
          return { user: action.tradingSigner.address }
        },
        onDone: [
          {
            guard: ({ event }) => event.output.approved,
            target: "evaluateOpenPlan",
          },
          {
            target: "approvingBuilder",
            actions: {
              type: "markPendingRetry",
              params: { target: "evaluateOpenPlan" as const },
            },
          },
        ],
        onError: {
          // Bail to idle on check failure — the order's lazy retry path
          // can't recover from this since the order isn't even submitted yet.
          target: "idle",
          actions: ["assignFailureError", "clearOpenWorkflow"],
        },
      },
    },

    evaluateOpenPlan: {
      always: [
        {
          guard: ({ context }) => shouldSetLeverage(context.openPlan),
          target: "settingLeverage",
        },
        {
          target: "opening",
        },
      ],
    },

    settingLeverage: {
      entry: "setSettingLeverageKind",
      invoke: {
        src: "updateLeverageActor",
        input: ({ context }) => {
          const action = assertOpenAction(context.openAction)
          return {
            tradingSigner: action.tradingSigner,
            orderAssetId: action.orderAssetId,
            selectedLeverage: action.selectedLeverage,
          }
        },
        onDone: [
          {
            guard: ({ event }) => event.output.ok === false,
            target: "idle",
            actions: ["assignFailureError", "clearOpenWorkflow"],
          },
          {
            target: "opening",
          },
        ],
        onError: {
          target: "idle",
          actions: ["assignFailureError", "clearOpenWorkflow"],
        },
      },
    },

    opening: {
      entry: ["setOpeningKind", "clearPendingRetry"],
      invoke: {
        src: "entryOrderActor",
        input: ({ context }) => {
          const action = assertOpenAction(context.openAction)
          const plan = assertValidOpenPlan(context.openPlan)
          return {
            tradingSigner: action.tradingSigner,
            orderAssetId: action.orderAssetId,
            szDecimals: action.szDecimals,
            isLong: action.isLong,
            entryOrder: action.entryOrder,
            requestedSize: plan.requestedSize,
            cloid: action.cloid,
            takeProfitPrice: action.takeProfitPrice,
            stopLossPrice: action.stopLossPrice,
            takeProfitCloid: action.takeProfitCloid,
            stopLossCloid: action.stopLossCloid,
          }
        },
        onDone: [
          {
            guard: ({ context, event }) =>
              !context.tx.builderApprovalAttempted &&
              event.output.ok === false &&
              needsBuilderApproval(event.output.error),
            target: "approvingBuilder",
            actions: {
              type: "markPendingRetry",
              params: { target: "opening" as const },
            },
          },
          {
            guard: ({ event }) => event.output.ok === false,
            target: "idle",
            actions: ["assignFailureError", "clearOpenWorkflow"],
          },
          {
            guard: ({ event }) => event.output.ok,
            target: "idle",
            actions: ["completeOpenJourney", "handleEntryOrderSuccess"],
          },
        ],
        onError: {
          target: "idle",
          actions: ["assignFailureError", "clearOpenWorkflow"],
        },
      },
    },

    planningAutoClose: {
      entry: "buildAutoClosePlan",
      always: [
        {
          guard: ({ context }) => isAutoCloseNoOp(context.autoClosePlan),
          target: "idle",
          actions: ["clearAutoCloseWorkflow"],
        },
        {
          guard: ({ context }) => hasAutoCloseCancels(context.autoClosePlan),
          target: "cancelingAutoClose",
        },
        {
          guard: ({ context }) => hasAutoCloseModifies(context.autoClosePlan),
          target: "modifyingAutoClose",
        },
        {
          guard: ({ context }) => hasAutoCloseAdditions(context.autoClosePlan),
          target: "addingAutoClose",
        },
        {
          target: "idle",
          actions: ["clearAutoCloseWorkflow"],
        },
      ],
    },

    cancelingAutoClose: {
      entry: "setUpdatingAutoCloseKind",
      invoke: {
        src: "cancelOrdersActor",
        input: ({ context }) => {
          const action = assertAutoCloseAction(context.autoCloseAction)
          const plan = assertAutoClosePlan(context.autoClosePlan)
          return {
            tradingSigner: action.tradingSigner,
            orderAssetId: action.orderAssetId,
            cancels: plan.cancels,
          }
        },
        onDone: [
          {
            guard: ({ event }) => event.output.ok === false,
            target: "idle",
            actions: ["assignFailureError", "clearAutoCloseWorkflow"],
          },
          {
            guard: ({ context }) => hasAutoCloseModifies(context.autoClosePlan),
            target: "modifyingAutoClose",
          },
          {
            guard: ({ context }) =>
              hasAutoCloseAdditions(context.autoClosePlan),
            target: "addingAutoClose",
          },
          {
            target: "idle",
            actions: ["emitAutoCloseUpdated", "clearAutoCloseWorkflow"],
          },
        ],
        onError: {
          target: "idle",
          actions: ["assignFailureError", "clearAutoCloseWorkflow"],
        },
      },
    },

    modifyingAutoClose: {
      entry: "setUpdatingAutoCloseKind",
      invoke: {
        src: "modifyTPSLActor",
        input: ({ context }) => {
          const action = assertAutoCloseAction(context.autoCloseAction)
          const plan = assertAutoClosePlan(context.autoClosePlan)
          return {
            ...action,
            modifies: plan.modifies,
          }
        },
        onDone: [
          {
            guard: ({ event }) => event.output.ok === false,
            target: "idle",
            actions: ["assignFailureError", "clearAutoCloseWorkflow"],
          },
          {
            guard: ({ context }) =>
              hasAutoCloseAdditions(context.autoClosePlan),
            target: "addingAutoClose",
          },
          {
            target: "idle",
            actions: ["emitAutoCloseUpdated", "clearAutoCloseWorkflow"],
          },
        ],
        onError: {
          target: "idle",
          actions: ["assignFailureError", "clearAutoCloseWorkflow"],
        },
      },
    },

    addingAutoClose: {
      entry: ["setUpdatingAutoCloseKind", "clearPendingRetry"],
      invoke: {
        src: "addTPSLActor",
        input: ({ context }) => {
          const action = assertAutoCloseAction(context.autoCloseAction)
          const plan = assertAutoClosePlan(context.autoClosePlan)
          return {
            ...action,
            additions: plan.additions,
          }
        },
        onDone: [
          {
            guard: ({ context, event }) =>
              !context.tx.builderApprovalAttempted &&
              event.output.ok === false &&
              needsBuilderApproval(event.output.error),
            target: "approvingBuilder",
            actions: {
              type: "markPendingRetry",
              params: { target: "addingAutoClose" as const },
            },
          },
          {
            guard: ({ event }) => event.output.ok === false,
            target: "reconcilingAddedAutoClose",
            actions: "assignFailureError",
          },
          {
            target: "idle",
            actions: ["emitAutoCloseUpdated", "clearAutoCloseWorkflow"],
          },
        ],
        onError: {
          target: "reconcilingAddedAutoClose",
          actions: "assignFailureError",
        },
      },
    },

    reconcilingAddedAutoClose: {
      invoke: {
        src: "reconcileAutoCloseActor",
        input: ({ context }) => ({
          kind: "add" as const,
          action: assertAutoCloseAction(context.autoCloseAction),
          plan: assertAutoClosePlan(context.autoClosePlan),
        }),
        onDone: [
          {
            guard: ({ event }) => event.output.updated,
            target: "idle",
            actions: [
              "clearTxResultAction",
              "emitAutoCloseUpdated",
              "clearAutoCloseWorkflow",
            ],
          },
          {
            target: "idle",
            actions: "clearAutoCloseWorkflow",
          },
        ],
        onError: {
          target: "idle",
          actions: "clearAutoCloseWorkflow",
        },
      },
    },

    closingPosition: {
      entry: ["setClosingPositionKind", "clearPendingRetry"],
      invoke: {
        src: "closePositionActor",
        input: ({ context }) => assertCloseAction(context.closeAction),
        onDone: [
          {
            guard: ({ context, event }) =>
              !context.tx.builderApprovalAttempted &&
              event.output.ok === false &&
              needsBuilderApproval(event.output.error),
            target: "approvingBuilder",
            actions: {
              type: "markPendingRetry",
              params: { target: "closingPosition" as const },
            },
          },
          {
            guard: ({ event }) => event.output.ok === false,
            target: "reconcilingClose",
            actions: "assignFailureError",
          },
          {
            target: "idle",
            actions: [
              "completeCloseJourney",
              "emitPositionClosed",
              "clearCloseWorkflow",
            ],
          },
        ],
        onError: {
          target: "reconcilingClose",
          actions: "assignFailureError",
        },
      },
    },

    reconcilingClose: {
      invoke: {
        src: "reconcileCloseActor",
        input: ({ context }) => assertCloseAction(context.closeAction),
        onDone: [
          {
            guard: ({ event }) => event.output.closed,
            target: "idle",
            actions: [
              "clearTxResultAction",
              "completeCloseJourney",
              "emitPositionClosed",
              "clearCloseWorkflow",
            ],
          },
          {
            target: "idle",
            actions: "clearCloseWorkflow",
          },
        ],
        onError: {
          target: "idle",
          actions: "clearCloseWorkflow",
        },
      },
    },

    approvingBuilder: {
      entry: "setApprovingBuilderKind",
      invoke: {
        src: "approveBuilderFeeActor",
        input: ({ context }) => {
          const tradingSigner =
            context.openAction?.tradingSigner ??
            context.autoCloseAction?.tradingSigner ??
            context.closeAction?.tradingSigner
          if (!tradingSigner) {
            throw new Error("No trading signer available for builder approval")
          }
          return { tradingSigner }
        },
        onDone: [
          {
            guard: ({ event }) => event.output.ok === false,
            target: "idle",
            actions: ["assignBuilderApprovalFailure", "clearAllWorkflows"],
          },
          {
            guard: ({ context }) =>
              context.tx.pendingRetry === "evaluateOpenPlan",
            target: "evaluateOpenPlan",
          },
          {
            guard: ({ context }) => context.tx.pendingRetry === "opening",
            target: "opening",
          },
          {
            guard: ({ context }) =>
              context.tx.pendingRetry === "addingAutoClose",
            target: "addingAutoClose",
          },
          {
            guard: ({ context }) =>
              context.tx.pendingRetry === "closingPosition",
            target: "closingPosition",
          },
          {
            target: "idle",
            actions: "clearAllWorkflowsAndPendingRetry",
          },
        ],
        onError: {
          target: "idle",
          actions: ["assignBuilderApprovalFailure", "clearAllWorkflows"],
        },
      },
    },
  },
})

function buildOpenPlan(input: OpenTradeActionInput): OpenActionPlan {
  const entryPrice = getEntryOrderPrice(input.entryOrder)
  if (entryPrice <= 0) {
    return {
      ok: false,
      error:
        input.entryOrder.type === "limit"
          ? "Limit price unavailable"
          : "Current price unavailable",
    }
  }

  if (input.selectedLeverage <= 0) {
    return { ok: false, error: "Leverage unavailable" }
  }

  const rawNotional = input.margin * input.selectedLeverage
  if (rawNotional < HL_MIN_ORDER_VALUE_USD) {
    return { ok: false, error: "Order below minimum size" }
  }
  let plan: ReturnType<typeof buildOpenOrderSizePlan>
  try {
    plan = buildOpenOrderSizePlan({
      margin: input.margin,
      leverage: input.selectedLeverage,
      entryPrice,
      szDecimals: input.szDecimals,
    })
  } catch {
    return { ok: false, error: "Order below minimum size" }
  }

  if (plan.quantizedNotional < HL_MIN_ORDER_VALUE_USD) {
    return { ok: false, error: "Order below minimum size" }
  }

  return {
    ok: true,
    requestedSize: plan.requestedSize,
    needsLeverageUpdate:
      input.currentLeverage == null ||
      input.currentLeverageMode !== "isolated" ||
      input.currentLeverage !== input.selectedLeverage,
    hasAutoClose:
      (input.takeProfitPrice ?? 0) > 0 || (input.stopLossPrice ?? 0) > 0,
  }
}

function setTxKind(
  context: PositionScreenContext,
  kind: PositionScreenContext["tx"]["kind"]
): PositionScreenContext["tx"] {
  return {
    ...context.tx,
    kind,
  }
}

function clearTxResult(
  context: PositionScreenContext
): PositionScreenContext["tx"] {
  return {
    ...context.tx,
    error: null,
    partialSuccess: null,
    failedKind: null,
    needsLeverage: false,
    pendingRetry: null,
    builderApprovalAttempted: false,
  }
}

function setTxError(
  context: PositionScreenContext,
  error: string,
  failedKind: PositionScreenContext["tx"]["failedKind"]
): PositionScreenContext["tx"] {
  return {
    ...context.tx,
    error,
    partialSuccess: null,
    failedKind,
  }
}

function setTxPartialSuccess(
  context: PositionScreenContext,
  partialSuccess: string,
  failedKind: PositionScreenContext["tx"]["failedKind"]
): PositionScreenContext["tx"] {
  return {
    ...context.tx,
    error: null,
    partialSuccess,
    failedKind,
  }
}

function isOpenPlanInvalid(plan: OpenActionPlan | null): boolean {
  return plan != null && !plan.ok
}

function shouldSetLeverage(plan: OpenActionPlan | null): boolean {
  return plan?.ok === true && plan.needsLeverageUpdate
}

function hasEntryAutoCloseError(data: EntryOrderResult): boolean {
  return data.autoCloseError != null && data.autoCloseError.trim() !== ""
}

function isAutoCloseNoOp(plan: AutoCloseActionPlan | null): boolean {
  return plan?.noOp === true
}

function hasAutoCloseCancels(plan: AutoCloseActionPlan | null): boolean {
  return (plan?.cancels.length ?? 0) > 0
}

function hasAutoCloseModifies(plan: AutoCloseActionPlan | null): boolean {
  return (plan?.modifies.length ?? 0) > 0
}

function hasAutoCloseAdditions(plan: AutoCloseActionPlan | null): boolean {
  return (plan?.additions.length ?? 0) > 0
}

function getUpdatedLegs(
  plan: AutoCloseActionPlan | null,
  action: SaveAutoCloseActionInput | null
): Array<"tp" | "sl"> {
  if (!plan || !action) {
    return []
  }

  const legs = new Set<"tp" | "sl">()

  for (const modify of plan.modifies) {
    legs.add(modify.leg)
  }
  for (const addition of plan.additions) {
    legs.add(addition.leg)
  }
  for (const cancel of plan.cancels) {
    const orderId = cancel.orderId
    const tpOrderId = action.currentTpOrder?.oid
    const slOrderId = action.currentSlOrder?.oid

    if (orderId === tpOrderId) {
      legs.add("tp")
    } else if (orderId === slOrderId) {
      legs.add("sl")
    }
  }

  return Array.from(legs)
}

function buildAutoClosePlan(
  input: SaveAutoCloseActionInput
): AutoCloseActionPlan {
  const cancels: AutoCloseActionPlan["cancels"] = []
  const modifies: AutoCloseActionPlan["modifies"] = []
  const additions: AutoCloseActionPlan["additions"] = []

  reconcileAutoCloseLeg(
    {
      currentOrder: input.currentTpOrder,
      desiredPrice: input.desiredTakeProfitPrice,
      leg: "tp",
    },
    { cancels, modifies, additions }
  )
  reconcileAutoCloseLeg(
    {
      currentOrder: input.currentSlOrder,
      desiredPrice: input.desiredStopLossPrice,
      leg: "sl",
    },
    { cancels, modifies, additions }
  )

  return {
    noOp:
      cancels.length === 0 && modifies.length === 0 && additions.length === 0,
    cancels,
    modifies,
    additions,
  }
}

function reconcileAutoCloseLeg(
  input: {
    currentOrder?: HLOpenOrder
    desiredPrice?: number
    leg: "tp" | "sl"
  },
  output: Pick<AutoCloseActionPlan, "cancels" | "modifies" | "additions">
) {
  const desiredPrice = input.desiredPrice ?? 0
  if (input.currentOrder) {
    if (desiredPrice <= 0) {
      output.cancels.push({ orderId: input.currentOrder.oid })
      return
    }

    if (Number(input.currentOrder.triggerPx) !== desiredPrice) {
      output.modifies.push({
        orderId: input.currentOrder.oid,
        leg: input.leg,
        price: desiredPrice,
      })
    }
    return
  }

  if (desiredPrice > 0) {
    output.additions.push({
      leg: input.leg,
      price: desiredPrice,
      cloid: createClientOrderId(),
    })
  }
}

function getActorResultError(event: unknown): string {
  const maybeEvent = event as {
    output?: { ok: boolean; error?: string }
    error?: unknown
  }

  if (maybeEvent.output?.ok === false) {
    return maybeEvent.output.error ?? "Unknown error"
  }
  if (maybeEvent.error instanceof Error) {
    return maybeEvent.error.message
  }
  return "Unknown error"
}

type EntryOrderSuccessOutcome = {
  kind: EntryOrderResult["kind"]
  autoCloseFailed: boolean
}

function getEntryOrderSuccessOutcome(event: unknown): EntryOrderSuccessOutcome {
  const data = assertEntryOrderSuccess(event)

  return {
    kind: data.kind,
    autoCloseFailed: hasEntryAutoCloseError(data),
  }
}

function assertEntryOrderSuccess(event: unknown): EntryOrderResult {
  const maybeEvent = event as {
    output?: Result<EntryOrderResult>
  }
  if (maybeEvent.output?.ok === true) {
    return maybeEvent.output.data
  }
  throw new Error("Expected successful entry order result")
}

function getEntryAutoClosePartialMessage(event: unknown): string {
  const data = assertEntryOrderSuccess(event)
  const error = data.autoCloseError
  const isResting = data.kind === "resting"
  return isResting
    ? `Limit order placed but TP/SL failed: ${error}`
    : `Position opened but TP/SL failed: ${error}`
}

async function reconcileCloseSubmission(
  input: ClosePositionActionInput
): Promise<{ closed: boolean }> {
  const user = input.tradingSigner.address
  if (!input.coin) {
    return { closed: false }
  }

  const account = await getAccountState(user, {
    dex: getDexFromCoin(input.coin),
  })
  if (!account.ok) {
    return { closed: false }
  }

  const position = account.data.positions.find((p) => p.coin === input.coin)
  return { closed: !position || Math.abs(Number(position.szi)) <= 1e-12 }
}

async function reconcileAutoCloseSubmission(
  input: AutoCloseReconcileInput
): Promise<AutoCloseReconcileResult> {
  const action = input.action
  const user = action.tradingSigner.address
  if (!action.coin) {
    return { updated: false, updatedLegs: [] }
  }

  const openOrders = await getOpenOrders(user, {
    dex: getDexFromCoin(action.coin),
  })
  if (!openOrders.ok) {
    return { updated: false, updatedLegs: [] }
  }

  const desiredLegs = getDesiredAutoCloseLegs(input)
  const updatedLegs = desiredLegs
    .filter((desired) =>
      openOrders.data.some((order) =>
        matchesAutoCloseOrder(order, action.coin as string, desired)
      )
    )
    .map((desired) => desired.leg)

  return {
    updated:
      desiredLegs.length > 0 && updatedLegs.length === desiredLegs.length,
    updatedLegs,
  }
}

type DesiredAutoCloseLeg = {
  leg: "tp" | "sl"
  cloid?: ClientOrderId
  isLong: boolean
  price: number
  size: string
}

function getDesiredAutoCloseLegs(
  input: AutoCloseReconcileInput
): DesiredAutoCloseLeg[] {
  return input.plan.additions.map((addition) => ({
    ...addition,
    isLong: input.action.isLong,
    size: input.action.size,
  }))
}

function matchesAutoCloseOrder(
  order: HLOpenOrder,
  coin: string,
  desired: DesiredAutoCloseLeg
): boolean {
  return (
    desired.cloid != null &&
    order.coin === coin &&
    order.cloid === desired.cloid &&
    order.reduceOnly &&
    order.isTrigger &&
    order.side === (desired.isLong ? "A" : "B") &&
    isAutoCloseOrderType(order, desired.leg) &&
    approximatelyEqualDecimal(order.triggerPx, desired.price, 1e-4) &&
    approximatelyEqualDecimal(order.sz, Number(desired.size), 1e-6)
  )
}

function isAutoCloseOrderType(order: HLOpenOrder, leg: "tp" | "sl"): boolean {
  if (leg === "tp") {
    return (
      order.orderType === "Take Profit Market" ||
      order.orderType === "Take Profit Limit"
    )
  }
  return order.orderType === "Stop Market" || order.orderType === "Stop Limit"
}

function approximatelyEqualDecimal(
  actualValue: string,
  expectedValue: number,
  relativeTolerance: number
): boolean {
  const actual = Number(actualValue)
  if (!Number.isFinite(actual) || !Number.isFinite(expectedValue)) {
    return false
  }
  const tolerance = Math.max(Math.abs(expectedValue) * relativeTolerance, 1e-12)
  return Math.abs(actual - expectedValue) <= tolerance
}

function assertOpenAction(
  value: OpenTradeActionInput | null
): OpenTradeActionInput {
  if (!value) {
    throw new Error("Missing open action")
  }
  return value
}

function assertValidOpenPlan(
  value: OpenActionPlan | null
): Extract<OpenActionPlan, { ok: true }> {
  if (!value || !value.ok) {
    throw new Error("Missing valid open plan")
  }
  return value
}

function assertAutoCloseAction(
  value: SaveAutoCloseActionInput | null
): SaveAutoCloseActionInput {
  if (!value) {
    throw new Error("Missing auto close action")
  }
  return value
}

function assertAutoClosePlan(
  value: AutoCloseActionPlan | null
): AutoCloseActionPlan {
  if (!value) {
    throw new Error("Missing auto close plan")
  }
  return value
}

function assertCloseAction(
  value: ClosePositionActionInput | null
): ClosePositionActionInput {
  if (!value) {
    throw new Error("Missing close action")
  }
  return value
}
